import { createContext, useEffect, useState, type ReactNode } from 'react';
import type { Integration, IntegrationState } from '@/types/integration';
import { IntegrationService } from '@/services/IntegrationService';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
// import { useTranslation } from 'react-i18next';
import { useTeams } from '@/hooks/useTeams';

// The context type definition has a few functions that weren't correctly implemented below.
// I've adjusted the implementation to match this type.
export interface IntegrationContextType {
  integrationState: IntegrationState;
  fetchIntegrations: (teamId: string) => Promise<void>;
  addIntegration: (teamId: string, integrationId: string, newIntegrationData: Partial<Integration>) => Promise<void>;
  updateIntegration: (teamId: string, integrationId: string, updates: Partial<Integration>) => Promise<void>;
  deleteIntegration: (teamId: string, integrationId: string) => Promise<void>;
  clearIntegrations: () => void;
  connectIntegration: (platform: string, teamId: string, code: string, state?: string,) => Promise<void>;
}

export const IntegrationContext = createContext<IntegrationContextType | undefined>(undefined);

const initialState: IntegrationState = {
  isLoading: false,
  integrations: [],
};

export const IntegrationProvider = ({ children }: { children: ReactNode }) => {
  // FIX 1: Renamed state variable to lowercase 'integrationState' for convention
  // and to avoid confusion with the 'IntegrationState' type.
  const [integrationState, setIntegrationState] = useState<IntegrationState>(initialState);
  const { handleError } = useApiErrorHandler();
  const { selectedTeam } = useTeams();
  // const { t } = useTranslation(['integrations']);

  // FIX 2: Renamed from 'getIntegrations' to match the context type.
  // Also simplified parameters to match the interface and added error handling for consistency.
  const fetchIntegrations = async (teamId: string) => {
    setIntegrationState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await IntegrationService.fetchIntegrations(teamId, 20, undefined);
      const newIntegrations = response.data || [];
      setIntegrationState(prev => ({
        ...prev,
        isLoading: false,
        integrations: newIntegrations,
      }));
    } catch (err) {
      handleError(err); // Added for consistency with other functions.
      setIntegrationState(prev => ({ ...prev, isLoading: false }));
    }
  };


  const addIntegration = async (teamId: string, integrationId: string, newIntegrationData: Partial<Integration>) => {
    setIntegrationState(prev => ({ ...prev, isLoading: true }));
    try {
      await IntegrationService.updateIntegration(teamId, integrationId, newIntegrationData);
      await fetchIntegrations(teamId); // Refresh list after adding
    } catch (err) {
      handleError(err);
      setIntegrationState(prev => ({ ...prev, isLoading: false }));
      throw err;
    }
  };

  const connectIntegration = async (
    platform: string,
    teamId: string,
    code: string,
    state?: string,
  ) => {
    setIntegrationState(prev => ({ ...prev, isLoading: true }));
    try {
      await IntegrationService.connectIntegration(platform, teamId, code, state);
    } catch (err) {
      handleError(err);
      throw err;
    } finally {
      setIntegrationState(prev => ({ ...prev, isLoading: false }));
    }
  }

  const updateIntegration = async (teamId: string, integrationId: string, updates: Partial<Integration>) => {
    setIntegrationState(prev => ({ ...prev, isLoading: true }));
    try {
      await IntegrationService.updateIntegration(teamId, integrationId, updates);
      await fetchIntegrations(teamId); // Refresh list after updating
    } catch (err) {
      handleError(err);
      setIntegrationState(prev => ({ ...prev, isLoading: false }));
      throw err;
    }
  };

  const deleteIntegration = async (teamId: string, integrationId: string) => {
    setIntegrationState(prev => ({ ...prev, isLoading: true }));
    try {
      await IntegrationService.deleteIntegration(integrationId);
      await fetchIntegrations(teamId); // Refresh list after deleting
    } catch (err) {
      handleError(err);
      setIntegrationState(prev => ({ ...prev, isLoading: false }));
      throw err;
    }
  };

  const clearIntegrations = () => {
    // No need for a try-catch on a simple state update.
    setIntegrationState(prev => ({
      ...prev,
      integrations: [],
    }));
  };

  useEffect(() => {
    if (!selectedTeam) {
      setIntegrationState(initialState); // optional: clear state when no team is selected
      return;
    }

    const initIntegration = async () => {
      try {
        await fetchIntegrations(selectedTeam.id);
      } catch (err: any) {
        handleError(err);
      }
    };

    initIntegration();
  }, [selectedTeam]);


  // FIX 4: Removed the problematic 'useEffect' and the unused 'getIntegration' function.
  // The initial data fetch should be triggered by a consumer component that has access to 'teamId'.
  // The 'getIntegration' function fetched a single item without adding it to the shared state,
  // making it unsuitable for this context provider.

  // FIX 5: Corrected the 'value' object to match the 'IntegrationContextType'.
  // It now provides the state under the 'integrationState' property and includes the correctly named functions.
  const value: IntegrationContextType = {
    integrationState,
    fetchIntegrations,
    addIntegration,
    updateIntegration,
    deleteIntegration,
    clearIntegrations,
    connectIntegration,
  };

  return (
    <IntegrationContext.Provider value={value}>
      {children}
    </IntegrationContext.Provider>
  );
};